import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { Composer } from '@/components/Composer';
import { InfiniteFeed } from '@/components/feed';
import { useRelayFeed, useFollowingFeed } from '@/hooks/useFeeds';
import { useFeed } from '@/contexts/FeedContext';
import { useSocial } from '@/contexts/SocialContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DEFAULT_RELAY_SETS } from '@/lib/jumble';

export function HomePage() {
  const { feed, setFeed, feedRelays } = useFeed();
  const { showReplies, setShowReplies } = useSocial();
  const { user } = useCurrentUser();

  useSeoMeta({
    title: 'Jumble — explore Nostr relay feeds',
    description: 'A user-friendly Nostr client for exploring relay feeds. Browse communities, follow people, post notes.',
  });

  const followingActive = feed.feedType === 'following';
  const following = useFollowingFeed({ enabled: followingActive });
  const relays = useRelayFeed(feedRelays, { showReplies, enabled: !followingActive });

  const active = followingActive ? following : relays;

  return (
    <AppShell
      title={feed.name ?? (followingActive ? 'Following' : 'Home')}
      right={
        <Button variant="ghost" size="sm" onClick={() => setShowReplies(!showReplies)} title="Toggle replies">
          Replies {showReplies ? 'on' : 'off'}
        </Button>
      }
    >
      {user && <Composer />}
      {!followingActive && feed.feedType !== 'relay' && (
        <div className="flex gap-2 overflow-x-auto border-b px-3 py-2">
          {user && (
            <Badge
              className="cursor-pointer whitespace-nowrap"
              variant={followingActive ? 'default' : 'secondary'}
              onClick={() => setFeed({ feedType: 'following', name: 'Following' })}
            >
              Following
            </Badge>
          )}
          {DEFAULT_RELAY_SETS.map((s) => (
            <Link key={s.id} to={`/feed/${s.id}`}>
              <Badge
                variant={feed.feedType === 'relays' && feed.id === s.id ? 'default' : 'secondary'}
                className="cursor-pointer whitespace-nowrap"
              >
                {s.name}
              </Badge>
            </Link>
          ))}
        </div>
      )}
      {followingActive && !user ? (
        <div className="p-6 text-center text-sm text-muted-foreground">Log in to see your following feed.</div>
      ) : (
        <InfiniteFeed
          events={active.events}
          isLoading={active.isLoading}
          isFetchingNextPage={active.isFetchingNextPage}
          hasNextPage={active.hasNextPage}
          fetchNextPage={() => active.fetchNextPage()}
        />
      )}
    </AppShell>
  );
}
